/*
 * Shared theme utilities. Loaded with `defer` BEFORE any component script
 * that consumes them, so they are available by the time component class
 * code runs.
 *
 * Exposed as window.OrdoUtils.* — keep this object stable; many components
 * delegate to it.
 */
(function () {
	if ( window.OrdoUtils ) return;

	/**
	 * Serialize a <form> into application/x-www-form-urlencoded format.
	 * Skips disabled, file, reset, submit, and button inputs. Honours
	 * `select-multiple` and unchecked checkboxes/radios.
	 */
	function serializeForm(form) {
		const arr = [];
		Array.prototype.slice.call(form.elements).forEach(function (field) {
			if (
				!field.name ||
				field.disabled ||
				['file', 'reset', 'submit', 'button'].indexOf(field.type) > -1
			) return;
			if (field.type === 'select-multiple') {
				Array.prototype.slice.call(field.options).forEach(function (option) {
					if (!option.selected) return;
					arr.push(
						encodeURIComponent(field.name) +
						'=' +
						encodeURIComponent(option.value)
					);
				});
				return;
			}
			if (['checkbox', 'radio'].indexOf(field.type) > -1 && !field.checked) return;
			arr.push(
				encodeURIComponent(field.name) + '=' + encodeURIComponent(field.value)
			);
		});
		return arr.join('&');
	}

	/**
	 * Parse an HTML response text into a Document. Used when fetching
	 * `?section_id=…` to extract a section's innerHTML.
	 */
	function parseHTML(text) {
		return new DOMParser().parseFromString(text, 'text/html');
	}

	/**
	 * A KROWN.settings.locales string as plain text, for textContent. Liquid's t filter
	 * HTML-escapes translations ("d&#39;abord"); innerHTML callers use the string as it is.
	 */
	function localeText(key) {
		const html = window.KROWN && KROWN.settings && KROWN.settings.locales && KROWN.settings.locales[key];
		return html ? parseHTML(html).body.textContent : '';
	}

	/**
	 * Format cents with a Shopify money format (the shop's own by default), then append the
	 * ISO code when the theme shows currency codes, the same order as Liquid's money | append.
	 */
	function formatMoney(cents, format) {
		if (typeof cents === 'string') {
			cents = cents.replace('.', '');
		}
		const settings = (window.KROWN && window.KROWN.settings) || {};
		const formatString = format || settings.shop_money_format || '{{amount}}';
		const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

		function formatWithDelimiters(number, precision, thousands, decimal) {
			thousands = thousands || ',';
			decimal = decimal || '.';
			if (isNaN(number) || number === null) {
				return 0;
			}
			const parts = (number / 100.0).toFixed(precision).split('.');
			const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
			const centsAmount = parts[1] ? decimal + parts[1] : '';
			return dollarsAmount + centsAmount;
		}

		let value = '';
		switch (formatString.match(placeholderRegex)[1]) {
			case 'amount':
				value = formatWithDelimiters(cents, 2);
				break;
			case 'amount_no_decimals':
				value = formatWithDelimiters(cents, 0);
				break;
			case 'amount_with_comma_separator':
				value = formatWithDelimiters(cents, 2, '.', ',');
				break;
			case 'amount_no_decimals_with_comma_separator':
				value = formatWithDelimiters(cents, 0, '.', ',');
				break;
			case 'amount_no_decimals_with_space_separator':
				value = formatWithDelimiters(cents, 0, ' ');
				break;
			case 'amount_with_apostrophe_separator':
				value = formatWithDelimiters(cents, 2, "'");
				break;
		}
		return formatString.replace(placeholderRegex, value) + (settings.iso_code || '');
	}

	/**
	 * Lock the page scroll behind a drawer or modal: remember the offset on
	 * <body data-scroll-y>, pin the body with a negative top and add the
	 * caller's body class. Locks are held by class name; the first one pins
	 * the body and the offset stays until the last one is released.
	 */
	const heldScrollLocks = new Set();

	function lockScroll(className) {
		if (heldScrollLocks.size === 0) {
			const scrollY = window.scrollY;
			document.body.dataset.scrollY = scrollY;
			document.body.style.top = `-${scrollY}px`;
		}
		heldScrollLocks.add(className);
		document.body.classList.add(className);
	}

	/**
	 * Undo lockScroll: release the named lock and drop its class. A lock that
	 * isn't held is ignored; the last release unpins the body and restores
	 * the offset.
	 */
	function unlockScroll(className) {
		if (!heldScrollLocks.has(className)) return;
		heldScrollLocks.delete(className);
		document.body.classList.remove(className);
		if (heldScrollLocks.size > 0) return;
		const scrollY = parseInt(document.body.dataset.scrollY || '0', 10);
		document.body.style.top = '';
		delete document.body.dataset.scrollY;
		window.scrollTo(0, scrollY);
	}

	/** Move keyboard focus to the cart drawer's close button once it has opened. */
	function focusCartClose() {
		setTimeout(function () {
			const drawer = document.getElementById('site-cart-sidebar');
			if (!drawer || !drawer._opened) return;
			const close = drawer.querySelector('[data-js-close]');
			if (close) close.focus();
		}, 200);
	}

	/**
	 * Cards put in after the page loads (Section Rendering, editor re-renders) never run quick-buy.liquid's inline
	 * script: move each form's variant input out of its template, unless the form already holds one.
	 */
	function hydrateQuickBuy(root) {
		root.querySelectorAll('form > template').forEach(function (template) {
			const form = template.parentElement;
			if (!form.querySelector('input[name="id"]')) form.append(template.content.cloneNode(true));
		});
	}

	/** Bazaarvoice stars the page's cards once (assets/component-quick-buy.js): ask it again for cards put in later. */
	function refreshRatings() {
		const bv = window.BV || window.$BV;
		try {
			if (bv && typeof bv.ui === 'function') bv.ui('rr', 'inline_ratings');
		} catch (e) { /* BV absent or a different API */ }
	}

	/**
	 * Cart requests run one at a time, in order: each posts, gets the
	 * helper-cart section (unless options.section is false), and the next
	 * starts only after that. A slow earlier response can't render over a
	 * later one. An object body goes as JSON, a string as is with
	 * options.headers. Resolves with { status, data, message, html } for any
	 * HTTP answer, where message is the text a 422 has for the customer and
	 * html is null when the section couldn't be fetched; rejects when the
	 * request itself fails or the job takes longer than 20 s.
	 */
	let cartQueue = Promise.resolve();

	// Ask for the helper-cart section in the same request, rendered for this page (bundled section rendering).
	function withCartSection(body, headers) {
		const params = { sections: 'helper-cart', sections_url: location.pathname };
		if (typeof body !== 'string') return JSON.stringify(Object.assign({}, body, params));
		if (/json/i.test(headers['Content-Type'])) {
			try { return JSON.stringify(Object.assign(JSON.parse(body), params)); } catch (e) { return body; }
		}
		return body + (body ? '&' : '') + new URLSearchParams(params).toString();
	}

	// A cart job still unanswered after 20 s is aborted, so it can't hold up the requests queued behind it.
	const cartTimeout = 20000;

	function cartRequest(url, body, options) {
		const opts = options || {};
		const job = cartQueue.then(async function () {
			const controller = new AbortController();
			const timer = setTimeout(function () { controller.abort(); }, cartTimeout);
			try {
				const headers = Object.assign({ 'Content-Type': 'application/json', 'Accept': 'application/json' }, opts.headers);
				const response = await fetch(url, {
					method: 'POST',
					headers: headers,
					body: opts.section === false ? (typeof body === 'string' ? body : JSON.stringify(body)) : withCartSection(body, headers),
					signal: controller.signal
				});
				const data = await response.json().catch(function () { return {}; });
				// A 422 is a stock or availability limit, worded for the customer; other failures aren't.
				const message = data.status === 422 ? (typeof data.description === 'string' ? data.description : data.message) : null;
				const result = { status: response.status, data: data, message: message, html: null };
				if (opts.section === false) return result;
				// Errors come without sections: fetch the section on its own then.
				if (data.sections && data.sections['helper-cart']) {
					result.html = data.sections['helper-cart'];
					return result;
				}
				// The request has been answered, so a failed section fetch leaves html null instead of failing it.
				try {
					const section = await fetch('?section_id=helper-cart', { signal: controller.signal });
					if (section.ok) result.html = await section.text();
				} catch (e) {}
				return result;
			} finally {
				clearTimeout(timer);
			}
		});
		cartQueue = job.catch(function () {});
		return job;
	}

	/** The helper-cart section on its own, fetched in turn with the other cart requests (window.refreshCart). */
	function cartRefresh() {
		const job = cartQueue.then(async function () {
			const controller = new AbortController();
			const timer = setTimeout(function () { controller.abort(); }, cartTimeout);
			try {
				const section = await fetch('?section_id=helper-cart', { signal: controller.signal });
				if (!section.ok) throw new Error('Cart section: HTTP ' + section.status);
				return await section.text();
			} finally {
				clearTimeout(timer);
			}
		});
		cartQueue = job.catch(function () {});
		return job;
	}

	/** The first line's product: cart recommendations are based on it. */
	function cartAnchor() {
		const first = document.querySelector('#AjaxCartForm [data-js-cart-item]');
		return first ? first.dataset.productId : null;
	}

	const cartFocusRoles = ['qty-plus', 'qty-minus', 'qty', 'remove'];

	function cartFocusSnapshot() {
		const active = document.activeElement;
		const item = active && active.closest && active.closest('#AjaxCartForm [data-js-cart-item]');
		if (!item) return null;
		const role = cartFocusRoles.find(function (r) { return active.classList.contains(r); });
		if (!role) return null;
		return { key: item.dataset.id, line: item.dataset.line, role: role, selectionStart: active.selectionStart, selectionEnd: active.selectionEnd };
	}

	/**
	 * Focus the same control on the same line, else on the line now in its place, else on the last line left. With no
	 * lines left: the drawer's close button, or the cart page's Continue browsing link.
	 */
	function cartRestoreFocus(snapshot) {
		if (!snapshot) return;
		const form = document.getElementById('AjaxCartForm');
		const items = Array.prototype.slice.call(document.querySelectorAll('#AjaxCartForm [data-js-cart-item]'));
		const same = items.find(function (i) { return i.dataset.id === snapshot.key; });
		const item = same
			|| items.find(function (i) { return i.dataset.line === snapshot.line; })
			|| items[items.length - 1];
		let target = item && item.querySelector('.' + snapshot.role);
		if (!item && form) {
			const drawer = form.closest('#site-cart-sidebar');
			target = drawer ? drawer.querySelector('[data-js-close]') : form.querySelector('a[href]');
		}
		if (!target) return;
		target.focus({ preventScroll: true });
		if (same && snapshot.role === 'qty' && snapshot.selectionStart != null) {
			try { target.setSelectionRange(snapshot.selectionStart, snapshot.selectionEnd); } catch (e) {}
		}
	}

	// Counts redraws, so an answer held back by one caller can tell whether another caller drew a newer cart since.
	let cartRenders = 0;

	/**
	 * Render a helper-cart section response into the cart drawer or cart page.
	 * Focus stays on the same line. Returns false when the response or the
	 * page has no cart.
	 */
	function cartRender(html) {
		if (!html) return false;
		const source = parseHTML(html);
		const formSource = source.getElementById('AjaxCartForm');
		const subtotalSource = source.getElementById('AjaxCartSubtotal');
		const cartForm = document.getElementById('AjaxCartForm');
		const subtotal = document.getElementById('AjaxCartSubtotal');
		if (!formSource || !subtotalSource || !cartForm || !subtotal) return false;

		const anchor = cartAnchor();
		const focus = cartFocusSnapshot();

		cartForm.innerHTML = formSource.innerHTML;
		if (typeof cartForm.ajaxifyCartItems === 'function') cartForm.ajaxifyCartItems();
		subtotal.innerHTML = subtotalSource.innerHTML;

		const count = cartForm.querySelector('[data-cart-count]');
		const total = cartForm.querySelector('[data-cart-total]');
		if (count) {
			document.querySelectorAll('[data-header-cart-count]').forEach(function (elm) { elm.textContent = count.textContent; });
		}
		if (total) {
			document.querySelectorAll('[data-header-cart-total]').forEach(function (elm) { elm.textContent = total.textContent; });
		}

		// #cart-wrapping sits outside the replaced regions: resync its checkbox with the new lines.
		const wrapping = document.querySelector('[data-js-cart-wrapping-checkbox]');
		if (wrapping) wrapping.checked = !!cartForm.querySelector('.cart-item--gift-wrapping');

		cartRestoreFocus(focus);

		// The recommendations follow the first line. An open drawer keeps its list (the shopper may be in it); the
		// next opening loads the new one.
		const recs = document.querySelector('cart-recommendations');
		const drawer = document.getElementById('site-cart-sidebar');
		if (recs && !(drawer && drawer._opened) && cartAnchor() !== anchor) recs.innerHTML = '';

		cartRenders++;
		// Every redraw is announced: the cart page updates its empty state on it.
		cartForm.dispatchEvent(new Event('cart-updated'));
		return true;
	}

	/**
	 * Announce a message in the cart's live region. Without one, it announces
	 * the rendered cart's item count and total.
	 */
	function cartAnnounce(message) {
		if (message === undefined) {
			const form = document.getElementById('AjaxCartForm');
			if (!form) return;
			const count = form.querySelector('[data-cart-count]');
			const total = form.querySelector('[data-cart-total]');
			const countText = count ? count.textContent.trim() : '';
			const totalText = total ? total.textContent.trim() : '';
			const template = localeText('cart_updated_announcement');
			message = template
				? template.replace('$COUNT', countText).replace('$TOTAL', totalText)
				: `Cart updated. ${countText} items, total ${totalText}.`;
		}
		let region = document.getElementById('CartLiveRegion');
		if (!region) {
			region = document.createElement('div');
			region.id = 'CartLiveRegion';
			region.setAttribute('role', 'status');
			region.setAttribute('aria-live', 'polite');
			region.setAttribute('aria-atomic', 'true');
			region.className = 'visually-hidden';
			document.body.appendChild(region);
		}
		// Cleared first, so an identical message is announced again.
		region.textContent = '';
		region.textContent = message;
	}

	/** Show an error above the cart lines, replacing the previous one, and announce it. */
	function cartError(message) {
		const form = document.querySelector('#AjaxCartForm form');
		if (form) {
			const previous = form.querySelector(':scope > .alert');
			if (previous) previous.remove();
			const alert = document.createElement('span');
			alert.className = 'alert alert--error';
			alert.textContent = message;
			form.prepend(alert);
		}
		cartAnnounce(message);
	}

	/*
	 * Link swatches (the product page colour row, compare columns) load a new page, so the
	 * tapped one takes the ring while that page loads (assets/component-product-variants.css).
	 */
	let swatchSwitchTimer = null;
	function clearSwatchSwitch() {
		clearTimeout(swatchSwitchTimer);
		document.querySelectorAll('[data-swatch-row].is-switching').forEach(function (row) {
			row.classList.remove('is-switching');
			row.querySelectorAll('.is-pressed').forEach(function (link) {
				link.classList.remove('is-pressed');
			});
		});
	}
	document.addEventListener('click', function (event) {
		if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
		const link = event.target.closest ? event.target.closest('a[data-swatch-link]') : null;
		const row = link ? link.closest('[data-swatch-row]') : null;
		if (!row || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
		clearSwatchSwitch();
		link.classList.add('is-pressed');
		row.classList.add('is-switching');
		// A navigation that never happens (Esc, a failed load) would leave the ring on the wrong swatch.
		swatchSwitchTimer = setTimeout(clearSwatchSwitch, 4000);
	});
	// Back/forward cache restores the page as it was left, mid-switch.
	window.addEventListener('pageshow', function (event) {
		if (event.persisted) clearSwatchSwitch();
	});

	window.OrdoUtils = {
		serializeForm: serializeForm,
		parseHTML: parseHTML,
		localeText: localeText,
		formatMoney: formatMoney,
		lockScroll: lockScroll,
		unlockScroll: unlockScroll,
		focusCartClose: focusCartClose,
		hydrateQuickBuy: hydrateQuickBuy,
		refreshRatings: refreshRatings,
		cart: {
			request: cartRequest,
			refresh: cartRefresh,
			render: cartRender,
			renders: function () { return cartRenders; },
			announce: cartAnnounce,
			error: cartError,
		},
	};
})();
